import React from "react";

import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import "./RegistrationLender.css";

import backgroundImg from "../../../../assets/background.png";

import logoImg from "../../../../assets/logo.png";

import LanguageSwitcher from "../../../../components/LanguageSwitcher";

import LoginButton from "../../../../components/layout/LoginButton";

import { EUROPEAN_COUNTRIES } from "../../../../data/countries";

import PasswordStrengthChecklist, {
  isStrongPassword,
} from "../../../../components/PasswordStrengthChecklist";

import { Eye, EyeOff } from "lucide-react";

import TermsConditionsModal from "../../legal/TermsConditionsModal";
import {
  normalizeVat,
  getLenderRegistrationErrors,
  validateLegalDocument,
} from "../../../../utils/registrationValidation";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LEGACY_REGISTRATION_STORAGE_KEY = "legup_lender_registration_form";

const REGISTRATION_STORAGE_KEY = "legup_lender_registration_form:v2";

const DEFAULT_FORM = {
  registration_mode: "CREATE_ORGANISATION",

  first_name: "",

  last_name: "",

  country: "",

  role_in_organisation: "",

  email: "",

  confirm_email: "",

  password: "",

  confirm_password: "",

  organisation_name: "",

  vat: "",

  company_key: "",

  notary_document: null,

  agree_terms: false,
};

const ROLE_OPTIONS = [
  { value: "", label: "Select role..." },

  { value: "LOAN_OFFICER", label: "Loan officer" },

  { value: "CREDIT_ANALYST", label: "Credit analyst" },

  { value: "BRANCH_MANAGER", label: "Branch manager" },

  { value: "UNDERWRITER", label: "Underwriter" },

  { value: "ADMIN", label: "Administrator" },

  { value: "OTHER", label: "Other" },
];

function CreateOrganisationFields({
  form,

  fieldErrors,

  onChange,

  t,

  showPassword,

  setShowPassword,

  showConfirmPassword,

  setShowConfirmPassword,
}) {
  return (
    <>
      <div className="lender-form-grid">
        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("firstName", "First name")}
          </label>

          <input
            name="first_name"
            placeholder={t("placeholders.firstName", "e.g., John")}
            value={form.first_name}
            onChange={onChange}
            className={fieldErrors.first_name ? "lender-input-error" : ""}
          />

          {fieldErrors.first_name && (
            <span className="lender-field-error">{fieldErrors.first_name}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("lastName", "Last name")}
          </label>

          <input
            name="last_name"
            placeholder={t("placeholders.lastName", "e.g., Smith")}
            value={form.last_name}
            onChange={onChange}
            className={fieldErrors.last_name ? "lender-input-error" : ""}
          />

          {fieldErrors.last_name && (
            <span className="lender-field-error">{fieldErrors.last_name}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("country", "Country")}
          </label>

          <select
            name="country"
            value={form.country}
            onChange={onChange}
            className={fieldErrors.country ? "lender-input-error" : ""}
          >
            <option value="">
              {t("placeholders.selectCountry", "Select a country...")}
            </option>

            {EUROPEAN_COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label}
              </option>
            ))}
          </select>

          {fieldErrors.country && (
            <span className="lender-field-error">{fieldErrors.country}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("roleInOrganisation", "Role in organisation")}
          </label>

          <select
            name="role_in_organisation"
            value={form.role_in_organisation}
            onChange={onChange}
            className={
              fieldErrors.role_in_organisation ? "lender-input-error" : ""
            }
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {t(`roles.${role.value}`, role.label)}
              </option>
            ))}
          </select>

          {fieldErrors.role_in_organisation && (
            <span className="lender-field-error">
              {fieldErrors.role_in_organisation}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("email", "Email address")}
          </label>

          <input
            type="email"
            name="email"
            placeholder={t("placeholders.email", "name@example.com")}
            value={form.email}
            onChange={onChange}
            className={fieldErrors.email ? "lender-input-error" : ""}
          />

          {fieldErrors.email && (
            <span className="lender-field-error">{fieldErrors.email}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("confirmEmail", "Confirm email")}
          </label>

          <input
            type="email"
            name="confirm_email"
            placeholder={t("placeholders.confirmEmail", "Repeat your email")}
            value={form.confirm_email}
            onChange={onChange}
            className={fieldErrors.confirm_email ? "lender-input-error" : ""}
          />

          {fieldErrors.confirm_email && (
            <span className="lender-field-error">
              {fieldErrors.confirm_email}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("password", "Password")}
          </label>

          <div className="lender-password-wrap">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t("placeholders.password", "Create a password")}
              value={form.password}
              onChange={onChange}
              className={fieldErrors.password ? "lender-input-error" : ""}
            />

            <button
              type="button"
              className="lender-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={
                showPassword
                  ? t("hidePassword", "Hide password")
                  : t("showPassword", "Show password")
              }
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <PasswordStrengthChecklist password={form.password} variant="light" />

          {fieldErrors.password && (
            <span className="lender-field-error">{fieldErrors.password}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("confirmPassword", "Confirm password")}
          </label>

          <div className="lender-password-wrap">
            <input
              name="confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t(
                "placeholders.confirmPassword",

                "Repeat your password",
              )}
              value={form.confirm_password}
              onChange={onChange}
              className={
                fieldErrors.confirm_password ? "lender-input-error" : ""
              }
            />

            <button
              type="button"
              className="lender-password-toggle"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={
                showConfirmPassword
                  ? t("hidePassword", "Hide password")
                  : t("showPassword", "Show password")
              }
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <PasswordStrengthChecklist
            password={form.confirm_password}
            variant="light"
          />

          {fieldErrors.confirm_password && (
            <span className="lender-field-error">
              {fieldErrors.confirm_password}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("organisationName", "Organisation name")}
          </label>

          <input
            name="organisation_name"
            placeholder={t("placeholders.organisationName", "e.g., Onesto")}
            value={form.organisation_name}
            onChange={onChange}
            className={
              fieldErrors.organisation_name ? "lender-input-error" : ""
            }
          />

          {fieldErrors.organisation_name && (
            <span className="lender-field-error">
              {fieldErrors.organisation_name}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">{t("vat", "VAT")}</label>

          <input
            name="vat"
            placeholder={t("placeholders.vat", "e.g., ESB12345678")}
            value={form.vat}
            onChange={onChange}
            className={fieldErrors.vat ? "lender-input-error" : ""}
          />

          {fieldErrors.vat && (
            <span className="lender-field-error">{fieldErrors.vat}</span>
          )}
        </div>

        <div className="lender-field lender-field--full lender-register-reveal-item">
          <label className="lender-field-label">
            {t("notaryDocument", "Notary document")}
          </label>

          <div className="lender-file-field">
            <span className="lender-file-field__text">
              {form.notary_document?.name ||
                t(
                  "placeholders.notaryDocument",

                  "Upload an official document confirming the legal representation of the organisation",
                )}
            </span>

            <label className="lender-file-field__button">
              {t("chooseFile", "Choose file")}

              <input
                type="file"
                name="notary_document"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={onChange}
                hidden
              />
            </label>
          </div>

          {fieldErrors.notary_document && (
            <span className="lender-field-error">
              {fieldErrors.notary_document}
            </span>
          )}
        </div>
      </div>

      <p className="lender-help-note lender-register-reveal-item">
        {t(
          "companyKeyGeneratedNote",

          "A unique company key will be automatically generated by the LEG-UP platform after registration. You can use this key later to invite additional users to your organisation.",
        )}
      </p>

      <p className="lender-help-note lender-register-reveal-item">
        {t(
          "marketParticipationNote",

          "The LEG-UP market regions in which your organisation participates will be configured by the Platform Administrator after registration.",
        )}
      </p>
    </>
  );
}

function JoinWithKeyFields({
  form,

  fieldErrors,

  onChange,

  t,

  showPassword,

  setShowPassword,

  showConfirmPassword,

  setShowConfirmPassword,
}) {
  return (
    <>
      <div className="lender-form-grid">
        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("firstName", "First name")}
          </label>

          <input
            name="first_name"
            placeholder={t("placeholders.firstName", "e.g., John")}
            value={form.first_name}
            onChange={onChange}
            className={fieldErrors.first_name ? "lender-input-error" : ""}
          />

          {fieldErrors.first_name && (
            <span className="lender-field-error">{fieldErrors.first_name}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("lastName", "Last name")}
          </label>

          <input
            name="last_name"
            placeholder={t("placeholders.lastName", "e.g., Smith")}
            value={form.last_name}
            onChange={onChange}
            className={fieldErrors.last_name ? "lender-input-error" : ""}
          />

          {fieldErrors.last_name && (
            <span className="lender-field-error">{fieldErrors.last_name}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("roleInOrganisation", "Role in organisation")}
          </label>

          <select
            name="role_in_organisation"
            value={form.role_in_organisation}
            onChange={onChange}
            className={
              fieldErrors.role_in_organisation ? "lender-input-error" : ""
            }
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {t(`roles.${role.value}`, role.label)}
              </option>
            ))}
          </select>

          {fieldErrors.role_in_organisation && (
            <span className="lender-field-error">
              {fieldErrors.role_in_organisation}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("email", "Email address")}
          </label>

          <input
            type="email"
            name="email"
            placeholder={t("placeholders.email", "name@example.com")}
            value={form.email}
            onChange={onChange}
            className={fieldErrors.email ? "lender-input-error" : ""}
          />

          {fieldErrors.email && (
            <span className="lender-field-error">{fieldErrors.email}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("confirmEmail", "Confirm email")}
          </label>

          <input
            type="email"
            name="confirm_email"
            placeholder={t("placeholders.confirmEmail", "Repeat your email")}
            value={form.confirm_email}
            onChange={onChange}
            className={fieldErrors.confirm_email ? "lender-input-error" : ""}
          />

          {fieldErrors.confirm_email && (
            <span className="lender-field-error">
              {fieldErrors.confirm_email}
            </span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("password", "Password")}
          </label>

          <div className="lender-password-wrap">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t("placeholders.password", "Create a password")}
              value={form.password}
              onChange={onChange}
              className={fieldErrors.password ? "lender-input-error" : ""}
            />

            <button
              type="button"
              className="lender-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={
                showPassword
                  ? t("hidePassword", "Hide password")
                  : t("showPassword", "Show password")
              }
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <PasswordStrengthChecklist password={form.password} variant="light" />

          {fieldErrors.password && (
            <span className="lender-field-error">{fieldErrors.password}</span>
          )}
        </div>

        <div className="lender-field lender-register-reveal-item">
          <label className="lender-field-label">
            {t("confirmPassword", "Confirm password")}
          </label>

          <div className="lender-password-wrap">
            <input
              name="confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t(
                "placeholders.confirmPassword",

                "Repeat your password",
              )}
              value={form.confirm_password}
              onChange={onChange}
              className={
                fieldErrors.confirm_password ? "lender-input-error" : ""
              }
            />

            <button
              type="button"
              className="lender-password-toggle"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={
                showConfirmPassword
                  ? t("hidePassword", "Hide password")
                  : t("showPassword", "Show password")
              }
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <PasswordStrengthChecklist
            password={form.confirm_password}
            variant="light"
          />

          {fieldErrors.confirm_password && (
            <span className="lender-field-error">
              {fieldErrors.confirm_password}
            </span>
          )}
        </div>

        <div className="lender-field lender-field--full lender-register-reveal-item">
          <label className="lender-field-label">
            {t("companyKey", "Company key")}
          </label>

          <input
            name="company_key"
            placeholder={t(
              "placeholders.companyKey",

              "Enter the company key shared with you",
            )}
            value={form.company_key}
            onChange={onChange}
            className={fieldErrors.company_key ? "lender-input-error" : ""}
          />

          {fieldErrors.company_key && (
            <span className="lender-field-error">
              {fieldErrors.company_key}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

export default function RegisterLenderPage() {
  const { t } = useTranslation("lender-registration");

  const { loginWithRedirect } = useAuth0();

  const [form, setForm] = React.useState(() => {
    try {
      const saved = sessionStorage.getItem(REGISTRATION_STORAGE_KEY);

      return saved
        ? {
            ...DEFAULT_FORM,

            ...JSON.parse(saved),

            password: "",

            confirm_password: "",

            notary_document: null,
          }
        : { ...DEFAULT_FORM };
    } catch (error) {
      console.error("Failed to restore lender registration form:", error);

      return { ...DEFAULT_FORM };
    }
  });

  const [loading, setLoading] = React.useState(false);

  const [err, setErr] = React.useState("");

  const [ok, setOk] = React.useState("");

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [companyKeyModal, setCompanyKeyModal] = React.useState({
    open: false,

    companyKey: "",

    organisationName: "",
  });

  const [showPassword, setShowPassword] = React.useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const [termsModalOpen, setTermsModalOpen] = React.useState(false);

  const [copySuccess, setCopySuccess] = React.useState(false);

  React.useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_REGISTRATION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear legacy lender registration draft:", error);
    }
  }, []);

  const closeTermsModal = React.useCallback(() => {
    setTermsModalOpen(false);
  }, []);

  function onChange(e) {
    const { name, value, type, checked, files } = e.target;
    let nextValue = type === "checkbox" ? checked : value;
    if (type === "file") {
      nextValue = files?.[0] || null;
      if (name === "notary_document" && nextValue) {
        const errorKey = validateLegalDocument(nextValue);
        if (errorKey) {
          e.target.value = "";
          setFieldErrors((prev) => ({ ...prev, notary_document: t(errorKey) }));
          return;
        }
      }
    }
    // Do not strip Unicode letters from names while users type.
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
      ...(name === "country" ? { vat: "" } : {}),
      ...(name === "registration_mode"
        ? {
            country: "",
            organisation_name: "",
            vat: "",
            notary_document: "",
            company_key: "",
          }
        : {}),
      ...(name === "email" ? { confirm_email: "" } : {}),
      ...(name === "password" ? { confirm_password: "" } : {}),
    }));
    if (err) setErr("");
  }

  function handleFieldBlur(event) {
    const { name, type } = event.target;
    if (!name || name === "registration_mode" || type === "radio") return;
    let nextValue =
      type === "file"
        ? form[name]
        : type === "checkbox"
          ? event.target.checked
          : event.target.value;
    if (name === "vat") {
      nextValue = normalizeVat(nextValue, form.country);
      if (nextValue !== form.vat)
        setForm((prev) => ({ ...prev, vat: nextValue }));
    }
    const errors = getLenderRegistrationErrors(
      { ...form, [name]: nextValue },
      isStrongPassword,
    );
    setFieldErrors((prev) => ({
      ...prev,
      [name]: errors[name] ? t(errors[name]) : "",
    }));
  }

  function validateStep1() {
    const errors = getLenderRegistrationErrors(form, isStrongPassword);
    return Object.fromEntries(
      Object.entries(errors).map(([field, key]) => [field, t(key)]),
    );
  }

  const AUTH_CONNECTION_KEY = "legup_auth_connection";

  function goToLogin() {
    const connection = "LEGUP-Lenders";

    sessionStorage.setItem(AUTH_CONNECTION_KEY, connection);

    loginWithRedirect({
      appState: {
        returnTo: "/post-login",
      },

      authorizationParams: {
        screen_hint: "login",

        prompt: "login",

        connection,

        ...(form.email?.trim()
          ? {
              login_hint: form.email.trim().toLowerCase(),
            }
          : {}),
      },
    });
  }

  async function submit(e) {
    e.preventDefault();

    setErr("");

    setOk("");

    setFieldErrors({});

    const errors = validateStep1();

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setErr(
        t("validation.fixErrors", "Please correct the highlighted fields."),
      );

      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("registration_mode", form.registration_mode);

      payload.append("first_name", form.first_name.trim());

      payload.append("last_name", form.last_name.trim());

      payload.append("role_in_organisation", form.role_in_organisation.trim());

      payload.append("email", form.email.trim().toLowerCase());

      payload.append("password", form.password);

      if (form.registration_mode === "CREATE_ORGANISATION") {
        payload.append("country", form.country.trim());

        payload.append("organisation_name", form.organisation_name.trim());

        payload.append("vat", normalizeVat(form.vat, form.country));

        if (form.notary_document) {
          payload.append("notary_document", form.notary_document);
        }
      }

      if (form.registration_mode === "JOIN_WITH_KEY") {
        payload.append("company_key", form.company_key.trim());
      }

      const res = await fetch(`${API_BASE}/api/register-lender/`, {
        method: "POST",

        body: payload,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const nextFieldErrors = {};

        if (data && typeof data === "object") {
          Object.entries(data).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              nextFieldErrors[key] = String(value[0]);
            } else if (value && typeof value === "string") {
              nextFieldErrors[key] = value;
            } else if (
              value &&
              typeof value === "object" &&
              !Array.isArray(value)
            ) {
              const firstNestedValue = Object.values(value)[0];

              if (
                Array.isArray(firstNestedValue) &&
                firstNestedValue.length > 0
              ) {
                nextFieldErrors[key] = String(firstNestedValue[0]);
              } else if (typeof firstNestedValue === "string") {
                nextFieldErrors[key] = firstNestedValue;
              }
            }
          });
        }

        if (Object.keys(nextFieldErrors).length > 0) {
          setFieldErrors(nextFieldErrors);
        }

        setErr(
          data?.message ||
            data?.detail ||
            nextFieldErrors.non_field_errors ||
            nextFieldErrors.country ||
            nextFieldErrors.email ||
            nextFieldErrors.company_key ||
            t("messages.failed", "Registration failed."),
        );

        return;
      }

      setFieldErrors({});

      sessionStorage.removeItem(REGISTRATION_STORAGE_KEY);

      localStorage.removeItem(LEGACY_REGISTRATION_STORAGE_KEY);

      if (
        form.registration_mode === "CREATE_ORGANISATION" &&
        data?.company_key
      ) {
        setOk(
          t(
            "messages.successWithCompanyKey",

            "Registration completed successfully.",
          ),
        );

        setCompanyKeyModal({
          open: true,

          companyKey: data.company_key,

          organisationName: data.organisation_name || form.organisation_name,
        });

        return;
      }

      setOk(
        t(
          "messages.success",

          "Registered successfully. Redirecting to login...",
        ),
      );

      setTimeout(() => {
        goToLogin();
      }, 1200);
    } catch (ex) {
      setErr(ex?.message || String(ex));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    try {
      const formToSave = {
        ...form,

        // Never persist credentials or browser File objects.

        password: "",

        confirm_password: "",

        notary_document: null,
      };

      sessionStorage.setItem(
        REGISTRATION_STORAGE_KEY,

        JSON.stringify(formToSave),
      );
    } catch (error) {
      console.error("Failed to save lender registration form:", error);
    }
  }, [form]);

  function closeCompanyKeyModalAndGoToLogin() {
    setCompanyKeyModal({
      open: false,

      companyKey: "",

      organisationName: "",
    });

    goToLogin();
  }

  async function handleCopyCompanyKey() {
    try {
      await navigator.clipboard.writeText(companyKeyModal.companyKey || "");

      setCopySuccess(true);

      setTimeout(() => {
        setCopySuccess(false);
      }, 1600);
    } catch (error) {
      console.error("Failed to copy company key:", error);
    }
  }

  return (
    <div className="lender-register-page">
      <div
        className="lender-register-bg-illustration lender-register-reveal-bg"
        style={{ backgroundImage: `url(${backgroundImg})` }}
      />

      <div className="lender-register-topbar lender-register-reveal lender-register-reveal--topbar">
        <Link to="/" className="lender-register-logo">
          <img src={logoImg} alt="LEG-UP" />
        </Link>

        <div className="lender-register-topbar__right">
          <LanguageSwitcher />

          <LoginButton />
        </div>
      </div>

      <div className="lender-register-shell">
        <div className="lender-register-card lender-register-reveal lender-register-reveal--card">
          <div className="lender-register-header lender-register-reveal-group">
            <div className="lender-register-header__main">
              <p className="lender-register-kicker">
                {t("kicker", "REGISTRATION")}
              </p>

              <h1>{t("title", "Create your LEG-UP lender account")}</h1>
            </div>

            <p className="lender-register-subtitle">
              {t(
                "subtitle",

                "Tell us who is registering and your organisation details. These will be used to create your lender profile and enable role-based access.",
              )}
            </p>
          </div>

          <div className="lender-register-note lender-register-reveal lender-register-reveal--note">
            {t(
              "note",

              "You can add more users later (multiple individuals under one organisation). For now, we just need your basic account and organisation information.",
            )}
          </div>

          <form
            className="lender-register-form-card lender-register-reveal lender-register-reveal--form"
            onSubmit={submit}
            onBlur={handleFieldBlur}
            noValidate
          >
            <h2 className="lender-section-title lender-register-reveal lender-register-reveal--section">
              {t("sectionTitle", "ACCOUNT DETAILS")}
            </h2>

            <div className="lender-step-transition lender-step-transition--enter">
              <div className="lender-radio-row">
                <div className="lender-radio-options">
                  <label className="lender-radio-option lender-register-reveal-item">
                    <input
                      type="radio"
                      name="registration_mode"
                      value="CREATE_ORGANISATION"
                      checked={form.registration_mode === "CREATE_ORGANISATION"}
                      onChange={onChange}
                    />

                    <span className="lender-radio-option__content">
                      <span className="lender-radio-option__title">
                        {t("createOrganisation", "Create organisation")}
                      </span>

                      <span className="lender-radio-option__subtitle">
                        {t(
                          "createOrganisationHelp",

                          "Register a new lender organisation and create its first administrator account.",
                        )}
                      </span>
                    </span>
                  </label>

                  <label className="lender-radio-option lender-register-reveal-item">
                    <input
                      type="radio"
                      name="registration_mode"
                      value="JOIN_WITH_KEY"
                      checked={form.registration_mode === "JOIN_WITH_KEY"}
                      onChange={onChange}
                    />

                    <span className="lender-radio-option__content">
                      <span className="lender-radio-option__title">
                        {t("joinWithKey", "Join with company key")}
                      </span>

                      <span className="lender-radio-option__subtitle">
                        {t(
                          "joinWithKeyHelp",

                          "Join an existing lender organisation using a shared company key.",
                        )}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div
                key={form.registration_mode}
                className="lender-animated-section lender-animated-section--active lender-register-reveal-mode"
              >
                {form.registration_mode === "CREATE_ORGANISATION" ? (
                  <CreateOrganisationFields
                    form={form}
                    fieldErrors={fieldErrors}
                    onChange={onChange}
                    t={t}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    showConfirmPassword={showConfirmPassword}
                    setShowConfirmPassword={setShowConfirmPassword}
                  />
                ) : (
                  <JoinWithKeyFields
                    form={form}
                    fieldErrors={fieldErrors}
                    onChange={onChange}
                    t={t}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    showConfirmPassword={showConfirmPassword}
                    setShowConfirmPassword={setShowConfirmPassword}
                  />
                )}
              </div>

              <div className="lender-terms-block lender-register-reveal lender-register-reveal--terms">
                <div className="lender-checkbox-row">
                  <input
                    id="lender-agree-terms"
                    type="checkbox"
                    name="agree_terms"
                    checked={form.agree_terms}
                    onChange={onChange}
                  />

                  <label
                    htmlFor="lender-agree-terms"
                    className="lender-terms-consent-text"
                  >
                    {t("termsCheckPrefix", "I agree to the")}
                  </label>

                  <button
                    type="button"
                    className="lender-terms-link"
                    onClick={() => setTermsModalOpen(true)}
                  >
                    {t("termsModal.link", "Terms and Conditions")}
                  </button>

                  <span className="lender-terms-required">\*</span>
                </div>

                {fieldErrors.agree_terms && (
                  <span className="lender-field-error">
                    {fieldErrors.agree_terms}
                  </span>
                )}
              </div>

              {(err || ok) && (
                <div className="lender-message-area">
                  {err && (
                    <div className="lender-message lender-message-error">
                      {err}
                    </div>
                  )}

                  {ok && (
                    <div className="lender-message lender-message-success">
                      {ok}
                    </div>
                  )}
                </div>
              )}

              <div className="lender-register-footer lender-register-reveal lender-register-reveal--footer">
                <Link to="/profile-selection" className="lender-back-link">
                  {t("back", "Back to profile selection")}
                </Link>

                <button
                  className="lender-submit-btn"
                  disabled={loading}
                  type="submit"
                >
                  {loading
                    ? t("creating", "Creating...")
                    : t("submit", "Submit")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <TermsConditionsModal open={termsModalOpen} onClose={closeTermsModal} />

      {companyKeyModal.open && (
        <div
          className="lender-company-key-modal__backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="company-key-modal-title"
        >
          <div className="lender-company-key-modal">
            <button
              type="button"
              className="lender-company-key-modal__close"
              onClick={closeCompanyKeyModalAndGoToLogin}
              aria-label={t("companyKeyModal.close", "Close")}
            >
              ×
            </button>

            <div className="lender-company-key-modal__header">
              <p className="lender-company-key-modal__kicker">
                {t("companyKeyModal.kicker", "REGISTRATION COMPLETED")}
              </p>

              <h2
                id="company-key-modal-title"
                className="lender-company-key-modal__title"
              >
                {t("companyKeyModal.title", "Your company key")}
              </h2>

              <p className="lender-company-key-modal__subtitle">
                {t(
                  "companyKeyModal.subtitle",

                  "Please save this company key now. You will use it later to allow additional lender users to join your organisation.",
                )}
              </p>
            </div>

            <div className="lender-company-key-modal__body">
              {companyKeyModal.organisationName ? (
                <div className="lender-company-key-modal__org">
                  <span className="lender-company-key-modal__org-label">
                    {t("companyKeyModal.organisation", "Organisation")}
                  </span>

                  <strong className="lender-company-key-modal__org-value">
                    {companyKeyModal.organisationName}
                  </strong>
                </div>
              ) : null}

              <div className="lender-company-key-modal__key-box">
                <span className="lender-company-key-modal__key-label">
                  {t("companyKeyModal.keyLabel", "Company key")}
                </span>

                <div className="lender-company-key-modal__key-value">
                  {companyKeyModal.companyKey}
                </div>
              </div>

              <p className="lender-company-key-modal__warning">
                {t(
                  "companyKeyModal.warning",

                  "This is shown only now after registration, so make sure to copy or store it in a safe place.",
                )}
              </p>

              <div className="lender-company-key-modal__market-info">
                <div className="lender-company-key-modal__market-info-icon">
                  i
                </div>

                <div className="lender-company-key-modal__market-info-content">
                  <strong>
                    {t(
                      "companyKeyModal.marketRegionsTitle",

                      "Market region setup",
                    )}
                  </strong>

                  <p>
                    {t(
                      "companyKeyModal.marketRegionsText",

                      "Your organisation's LEG-UP market regions are assigned by the Platform Administrator. Please contact us and let us know which regions your organisation covers.",
                    )}
                  </p>

                  <a href="mailto:admin@renoassist.eu">admin@renoassist.eu</a>
                </div>
              </div>
            </div>

            <div className="lender-company-key-modal__actions">
              <button
                type="button"
                className="lender-company-key-modal__btn lender-company-key-modal__btn--secondary"
                onClick={handleCopyCompanyKey}
              >
                {copySuccess
                  ? t("companyKeyModal.copied", "Copied")
                  : t("companyKeyModal.copy", "Copy key")}
              </button>

              <button
                type="button"
                className="lender-company-key-modal__btn lender-company-key-modal__btn--primary"
                onClick={closeCompanyKeyModalAndGoToLogin}
              >
                {t("companyKeyModal.continue", "Continue to login")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
